import {NgClass} from "@angular/common"
import {Component, computed, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {ArDialogComponent} from "@app/common/components/dialogs/ar-dialog/ar-dialog.component"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {ActionMenuService} from "@app/common/components/viewers/configurator/action-menu/services/action-menu.service"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {getParametersForArGeneration} from "@app/common/helpers/ar/ar"
import {ArService} from "@app/common/components/viewers/configurator/services/ar.service"
import {isFullscreenApiAvailable} from "@app/common/helpers/fullscreen/fullscreen"
import {GltfExportService} from "@app/common/services/gltf-export/gltf-export.service"
import {ThreeTemplateSceneProviderComponent} from "@app/template-editor/components/three-template-scene-provider/three-template-scene-provider.component"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {TippyDirective} from "@ngneat/helipopper"
import {TemplateSceneProviderComponent} from "@app/template-editor/components/template-scene-provider/template-scene-provider.component"
import {MatTooltip} from "@angular/material/tooltip"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-action-menu",
    templateUrl: "./action-menu.component.html",
    styleUrls: ["./action-menu.component.scss"],
    imports: [
        NgClass,
        TippyDirective,
        LoadingSpinnerIconComponent,
        ArDialogComponent,
        ThreeTemplateSceneProviderComponent,
        TemplateSceneProviderComponent,
        MatTooltip,
    ],
})
export class ActionMenuComponent implements OnInit {
    readonly $inFullscreen = input.required<boolean>({
        alias: "inFullscreen",
    })
    readonly $fullscreenEnabled = input<boolean>(false, {
        alias: "fullscreenEnabled",
    })
    readonly $arEnabled = input<boolean>(false, {
        alias: "arEnabled",
    })
    readonly $stlDownloadEnabled = input<boolean>(false, {
        alias: "stlDownloadEnabled",
    })
    readonly $gltfDownloadEnabled = input<boolean>(false, {
        alias: "gltfDownloadEnabled",
    })
    readonly $snapshotEnabled = input<boolean>(false, {
        alias: "snapshotEnabled",
    })
    readonly $pdfEnabled = input<boolean>(false, {
        alias: "pdfEnabled",
    })
    readonly $enableDimensionGuides = input<boolean>(false, {
        alias: "enableDimensionGuides",
    })
    readonly $dimensionGuidesActive = input<boolean>(false, {
        alias: "dimensionGuidesActive",
    })
    $organizationId = input<string | undefined>(undefined, {
        alias: "organizationId",
    })

    readonly $tpOffset = signal<[number, number]>([0, 20])
    readonly $desktopArUrl = signal<string | undefined>(undefined)

    protected readonly sceneManagerService = inject(SceneManagerService)
    private readonly arService = inject(ArService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly configMenuService = inject(ConfigMenuService)
    protected readonly actionMenuService = inject(ActionMenuService)
    protected localSceneManagerService: SceneManagerService | undefined
    protected readonly threeSceneManagerService = inject(ThreeSceneManagerService)
    protected localThreeSceneManagerService: ThreeSceneManagerService | undefined
    protected readonly gltfExportService = inject(GltfExportService)

    readonly $fullscreenAvailable = computed(() => {
        return isFullscreenApiAvailable() && this.$fullscreenEnabled()
    })

    readonly $pendingTasksDescriptor = computed(() => {
        const numPendingTasks = this.sceneManagerService.$numPendingTasks()
        if (numPendingTasks) {
            return `Loading ${numPendingTasks} tasks`
        }
        const numTilesLoading = this.threeSceneManagerService.materialManagerService.textureTilesManager.$numTilesLoading()
        if (numTilesLoading) {
            return `Loading ${numTilesLoading} texture tiles`
        }
        return undefined
    })

    readonly $showMenu = computed(() => {
        return (
            this.$fullscreenAvailable() ||
            this.$arEnabled() ||
            this.$stlDownloadEnabled() ||
            this.$snapshotEnabled() ||
            this.$pendingTasksDescriptor() ||
            this.$pdfEnabled()
        )
    })

    constructor() {
        this.arService.desktopArUrl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((desktopArUrl) => {
            this.$desktopArUrl.set(desktopArUrl)
        })

        this.configMenuService.configSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.$desktopArUrl.set(undefined)
        })
    }

    ngOnInit(): void {}

    toggleFullscreen() {
        this.actionMenuService.toggleFullscreen()
    }

    setSceneManagerService(sceneManagerService: SceneManagerService | undefined) {
        this.localSceneManagerService = sceneManagerService
    }

    async exportStlClicked() {
        if (!this.localSceneManagerService) throw new Error("SceneManagerService not set")
        this.actionMenuService.downloadStl(
            this.sceneManagerService.$templateGraph(),
            this.sceneManagerService.$instanceParameters(),
            this.sceneManagerService.$sceneOptions(),
            this.localSceneManagerService,
        )
    }

    async exportGltfClicked() {
        if (!this.localThreeSceneManagerService) throw new Error("ThreeSceneManagerService not set")
        this.actionMenuService.downloadGltf(
            this.sceneManagerService.$templateGraph(),
            this.sceneManagerService.$instanceParameters(),
            this.sceneManagerService.$sceneOptions(),
            this.localThreeSceneManagerService,
        )
    }

    async viewInArClicked() {
        const templateRevisionId = this.sceneManagerService.$templateRevisionId()
        if (!templateRevisionId) return
        void this.arService.viewArModel(
            templateRevisionId,
            getParametersForArGeneration(this.sceneManagerService),
            this.sceneManagerService.$verticalArPlacement(),
        )
    }

    async downloadPdf() {
        this.actionMenuService.emitPdfDownloadRequested()
    }

    toggleDimensionGuides() {
        this.actionMenuService.toggleDimensionGuides()
    }

    setThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        this.localThreeSceneManagerService = threeSceneManagerService
    }
}
